import React from 'react';
import { Button } from '../ui/button';
import { ArrowLeft } from 'lucide-react';
import { PRIVATE_BETA_ENABLED } from '@/config/api';

interface LandingHeaderProps {
  showBackButton?: boolean;
  currentPage?: string; // Kept for backward compatibility, but not used (all links go to marketing site)
  variant?: 'light' | 'transparent';
}

// Base URL for the marketing website
const MARKETING_SITE_URL = 'https://www.pagemind.ai';

export const LandingHeader: React.FC<LandingHeaderProps> = ({
  showBackButton = true,
  variant = 'light'
}) => {

  const handleGetStarted = () => {
    // Get Started should go to onboarding.pagemind.ai
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const onboardingUrl = isLocalhost
      ? 'http://localhost:3000/signup'  // Use local admin signup
      : 'https://onboarding.pagemind.ai/signup';
    window.location.href = onboardingUrl;
  };

  // All navigation links point to the marketing website (www.pagemind.ai)
  const navLinks = [
    { href: MARKETING_SITE_URL, label: 'Home' },
    { href: `${MARKETING_SITE_URL}/features`, label: 'Features' },
    { href: `${MARKETING_SITE_URL}/demo/aeo-impact`, label: 'Demo' },
    ...(!PRIVATE_BETA_ENABLED ? [{ href: `${MARKETING_SITE_URL}/pricing`, label: 'Pricing' }] : []),
    { href: `${MARKETING_SITE_URL}/partners`, label: 'Partners' },
    { href: `${MARKETING_SITE_URL}/contact`, label: 'Contact' },
  ];

  const displayNavLinks = navLinks;

  const isTransparent = variant === 'transparent';
  const textColorClass = isTransparent ? 'text-white' : 'text-gray-700';
  const textHoverClass = isTransparent ? 'hover:text-white' : 'hover:text-gray-900';

  return (
    <header className={isTransparent ? 'absolute top-0 left-0 right-0 z-30 bg-transparent' : 'bg-white shadow-sm'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:py-6">
          <div className="flex items-center">
            {showBackButton && !isTransparent && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = MARKETING_SITE_URL}
                className="mr-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            <a href={MARKETING_SITE_URL}>
              <img
                src={isTransparent ? "/pagemind-logo-signup.png" : "/pagemind-logo-black.png"}
                alt="PageMind"
                className={`h-8 md:h-10 w-auto ${isTransparent ? 'drop-shadow-md' : ''}`}
              />
            </a>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            {displayNavLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`${textColorClass} ${textHoverClass} ${isTransparent ? 'drop-shadow-md' : ''}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://admin.pagemind.ai/login"
              className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isTransparent
                  ? "border border-white/70 text-white bg-white/10 backdrop-blur-sm hover:bg-white hover:text-black"
                  : "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              }`}
            >
              Login
            </a>
            <Button 
              size="sm"
              onClick={handleGetStarted}
              className={isTransparent ? "bg-white text-black hover:bg-white/90" : ""}
            >
              Get Started
            </Button>
          </nav>
          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button 
              size="sm"
              onClick={handleGetStarted}
              className={isTransparent ? "bg-white text-black hover:bg-white/90" : ""}
            >
              Get Started
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};